import {ZyusetuFieldKeyFragmentFragment, ZyusetuFieldKeyType} from "@/graphql/graphql";
import {FormSchema} from "@/components/pages/Zyusetu/ZyusetuEditorSection.tsx";
import {Controller, useFormContext, useWatch} from "react-hook-form";
import {Box, Button, FormHelperText, Grid2, IconButton, TextField, Tooltip, Typography} from "@mui/material";
import {Plus, X} from "lucide-react";
import {zyusetuValueIntoZodValue} from "@/hooks/useGetFirstContent";
import {ZyusetuEditType} from "@/types/ZyusetuEditType.ts";

interface ZyusetuTenantFieldProps {
  fieldKey: ZyusetuFieldKeyFragmentFragment;
  onFocus: () => void;
  updateField: (
    keyId: string,
    keyName: string,
    value: FormSchema<ZyusetuFieldKeyType.Tenant>[string],
  ) => Promise<void>;
  isAvailable: boolean;
  editType?: ZyusetuEditType
}

interface TenantValue {
  tenant_name: string;
  tenant_age: string;
}

export function ZyusetuTenantField(
  {
    fieldKey,
    updateField,
    isAvailable,
    onFocus,
    editType
  }: ZyusetuTenantFieldProps
) {
  const {control, setValue, getValues, formState: {errors}} = useFormContext<FormSchema<ZyusetuFieldKeyType.Tenant>>();
  
  const watch = useWatch({
    name: `${fieldKey.keyName}.value`,
    control
  });

  const handleAppend = () => {
    const currentValues = getValues(`${fieldKey.keyName}.value`) as TenantValue[] || [];
    setValue(`${fieldKey.keyName}.value`, [
      ...currentValues,
      {tenant_name: '', tenant_age: ''}
    ]);
  };

  const handleRemove = (index: number) => {
    const currentValues = getValues(`${fieldKey.keyName}.value`) as TenantValue[] || [];
    setValue(
      `${fieldKey.keyName}.value`,
      currentValues.filter((_, i) => i !== index)
    );
  };

  const values = watch || [];
  
  return (
    <>
      {values.map((_, index) => (
        <Grid2 container spacing={2} key={`${fieldKey.keyName}-tenant-input-${index}`} mt={2}>
          <Grid2 size={5}>
            <Typography variant="formLabel">
              名前
            </Typography>
            <Controller
              name={`${fieldKey.keyName}.value.${index}.tenant_name`}
              control={control}
              render={({field: fieldRender}) => (
                <TextField
                  {...fieldRender}
                  onFocus={onFocus}
                  placeholder={"名前"}
                  fullWidth
                  error={!!errors?.[fieldKey.keyName]?.value?.[index]?.tenant_name?.message}
                  helperText={errors?.[fieldKey.keyName]?.value?.[index]?.tenant_name?.message as string}
                  disabled={!isAvailable}
                  size={'small'}
                />
              )}
            />
          </Grid2>
          <Grid2 size={3}>
            <Typography variant="formLabel">
              年齢
            </Typography>
            <Controller
              name={`${fieldKey.keyName}.value.${index}.tenant_age`}
              control={control}
              render={({field: fieldRender}) => (
                <TextField
                  {...fieldRender}
                  placeholder={"年齢"}
                  error={!!errors?.[fieldKey.keyName]?.value?.[index]?.tenant_age?.message}
                  helperText={errors?.[fieldKey.keyName]?.value?.[index]?.tenant_age?.message as string}
                  fullWidth
                  disabled={!isAvailable}
                  size={'small'}
                />
              )}
            />
          </Grid2>
          <Grid2 size={3} sx={{display: 'flex', alignItems: 'end'}}>
            <Tooltip title={'削除'}>
              <IconButton
                tabIndex={-1}
                onClick={() => handleRemove(index)}
                disabled={!isAvailable || editType === ZyusetuEditType.ReadOnly}
                size={'small'}
              >
                <X/>
              </IconButton>
            </Tooltip>
          </Grid2>
        </Grid2>
      ))}
      <Box sx={{textAlign: 'center', mt: 3}}>
        <Button
          variant={'contained'}
          tabIndex={-1}
          onClick={handleAppend}
          disabled={values.length >= 6 || editType === ZyusetuEditType.ReadOnly}
        >
          <Plus/>
          追加
        </Button>
        <Button
          disabled={editType === ZyusetuEditType.ReadOnly}
          variant={'text'}
          tabIndex={-1}
          onClick={async () => {
            if (errors?.[fieldKey.keyName]?.value?.message) return;
            
            await updateField(
              fieldKey.id,
              fieldKey.keyName,
              zyusetuValueIntoZodValue(
                fieldKey,
                watch
              ) as FormSchema<ZyusetuFieldKeyType.Tenant>[string]
            );
          }}
        >
          保存
        </Button>
      </Box>
          {errors?.[fieldKey.keyName]?.value?.message &&
            <FormHelperText style={{color: "red"}}>
              {errors?.[fieldKey.keyName]?.value?.message || ""}
            </FormHelperText>}
    </>
  );
}
